import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col, Card, CardBody, Button, Form, FormGroup, Label, Input, Table, Spinner } from "reactstrap";
import { FaTruck, FaSearch, FaFilePdf, FaFilter, FaFileDownload } from "react-icons/fa";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useTranslation } from "react-i18next";
import { format, formatDate } from "date-fns";
import { deliveryActions } from "../../sagas/deliveryNoteSlice";
import { debounce } from "lodash";
import { formatPrice } from "../../helpers/utility";
import { productActions } from "../../sagas/productSlice";

const BLDetails = () => {
    const { lignes, loading, error } = useSelector((state) => state.deliveryNotes);
    const products = useSelector(state => state.products.products);
    const { company } = useSelector(state => state.auth);
    const unites = useSelector(status => status.products.unites); // Assuming you have a Redux store with unites

    const { t } = useTranslation();
    // Get start and end of current month as yyyy-MM-dd
    const startDate = format(new Date(), 'yyyy-MM-dd');
    const endDate = format(new Date(Date.now() + 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
    const [filters, setFilters] = useState({
        articleId: null,
        dateFrom: startDate,
        dateTo: endDate,
        query: ''
    });

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(deliveryActions.findLigneBLReport(filters));
        dispatch(productActions.findAllUnites());
    }, [dispatch, filters]);


    const handleExport = () => {
        if (!lignes || lignes.length === 0) return;
        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
        doc.setProperties({ title: t('delivery.reportTitle') });

        // Colors
        const primaryColor = [212, 175, 55]; // Gold
        const lightGray = [250, 250, 230]; // Light gold/ivory

        // Company title bar
        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, doc.internal.pageSize.getWidth(), 16, 'F');
        doc.setFontSize(18);
        doc.setTextColor(60, 50, 10); // Dark gold text
        doc.setFont('helvetica', 'bold');
        const companyTitle = company?.name || t('company.title', 'Ma Société');
        doc.text(companyTitle, doc.internal.pageSize.getWidth() / 2, 11, { align: 'center' });

        // Report title
        doc.setFontSize(13);
        doc.setTextColor(212, 175, 55); // Gold
        doc.setFont('helvetica', 'bold');
        doc.text('Rapport des ventes', doc.internal.pageSize.getWidth() / 2, 22, { align: 'center' });

        // Dates section as colored boxes
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const dateGen = new Date();
        const dateGenStr = dateGen.toLocaleDateString('fr-FR') + ' ' + dateGen.toLocaleTimeString('fr-FR');
        const dateFromStr = filters.dateFrom;
        const dateToStr = filters.dateTo;
        // Draw boxes
        doc.setFillColor(...lightGray);
        doc.rect(10, 26, 60, 10, 'F');
        doc.rect(80, 26, 60, 10, 'F');
        doc.rect(160, 26, 60, 10, 'F');
        doc.setTextColor(212, 175, 55); // Gold
        doc.text(`${t('report.dateFrom', 'Période du')}:`, 13, 32);
        doc.text(`${t('report.dateTo', 'Au')}:`, 83, 32);
        doc.text(`${t('report.generatedAt', 'Généré le')}:`, 163, 32);
        doc.setTextColor(60, 50, 10); // Dark gold for values
        doc.text(dateFromStr, 40, 32);
        doc.text(dateToStr, 110, 32);
        doc.text(dateGenStr, 185, 32);

        // Table headers
        const headers = [[
            t('delivery.numeroBL', 'N° BL'),
            t('delivery.dateOperation', 'Date opération'),
            t('product.code', 'Code article'),
            t('product.description1', 'Libellé article'),
            t('devis.quantite', 'Quantité'),
            t('devis.unite', 'Unité')
        ]];

        // Table body
        const body = lignes.map(m => [
            m.blNumero,
            m.blDateCreation,
            m.articleCode,
            `${m.articleCategorie} - ${m.articleNom}`,
            m.quantiteLivree,
            unites.find(u => u.code === m.unite)?.libelle || m.unite
        ]);

        // Table summary row
        const totalQuantite = lignes.reduce((sum, m) => sum + (Number(m.quantiteLivree) || 0), 0);
        // Add summary row at the end
        if (body.length > 0) {
            body.push([
                '', '', '', t('common.total', 'Total'), totalQuantite, ''
            ]);
        }

        autoTable(doc, {
            head: headers,
            body: body,
            startY: 42,
            styles: { fontSize: 10, cellPadding: 2 },
            headStyles: { fillColor: primaryColor, textColor: [60, 50, 10], fontStyle: 'bold', halign: 'center' },
            bodyStyles: { halign: 'center' },
            alternateRowStyles: { fillColor: [255, 248, 220] }, // light gold
            margin: { left: 10, right: 10 },
            theme: 'grid',
            didDrawPage: function (data) {
                // Custom footer
                doc.setFontSize(9);
                doc.setTextColor(212, 175, 55); // Gold
                doc.text('Document généré par le système le ' + dateGenStr, 10, doc.internal.pageSize.getHeight() - 8);
            }
        });

        const pdfBlob = doc.output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl);
    }

    const debouncedSearch = useCallback(
        debounce((query) => {
            dispatch(deliveryActions.findLigneBLReport({
                ...filters,
                query: query
            }));
        }, 1000), // Wait 1 second after user stops typing
        [filters.query]
    );

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setFilters(prevFilters => ({
            ...prevFilters,
            query: value
        }));
        debouncedSearch(value);
    };

    const clearFilters = () => {
        setFilters({
            query: '',
            articleId: null,
            dateFrom: formatDate(startDate),
            dateTo: formatDate(endDate)
        });
    };




    return (
        <Container fluid>
            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaTruck className="text-primary me-2" />
                                {t('delivery.reportTitle')}
                            </h4>
                            <div className="d-flex align-items-center">
                                <Button
                                    color="danger"
                                    onClick={() => handleExport()}
                                    className="me-2"
                                >
                                    <FaFileDownload className="me-2" />
                                    {t('delivery.blPrint')}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            {/* Search Filters */}
            <Row className="mb-3">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody>
                            <Row className="mb-3 g-2 align-items-end">
                                <Col lg={5} md={4} sm={6} xs={12}>
                                    <FormGroup className="mb-0">
                                        <Label className="mb-1">{t('delivery.search')}</Label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light">
                                                <FaSearch />
                                            </span>
                                            <Input
                                                type="text"
                                                placeholder={t('reglement.searchPlaceholder')}
                                                value={filters.query}
                                                onChange={e => {
                                                    e.target.value = e.target.value.toUpperCase();
                                                    handleSearchChange(e);
                                                }}
                                                style={{ textTransform: 'uppercase' }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleSearchChange();
                                                    }
                                                }}
                                            />
                                        </div>
                                    </FormGroup>
                                </Col>
                                <Col md={3}>
                                    <FormGroup>
                                        <Label for="dateFrom">Date début</Label>
                                        <Input
                                            type="date"
                                            id="dateFrom"
                                            value={filters.dateFrom}
                                            onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={3}>
                                    <FormGroup>
                                        <Label for="dateTo">Date fin</Label>
                                        <Input
                                            type="date"
                                            id="dateTo"
                                            value={filters.dateTo}
                                            onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={1} >
                                    <Button
                                        color="secondary"
                                        onClick={clearFilters}
                                        title={t('common.clearFilters')}
                                        className="mb-3"
                                        style={{ minHeight: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                        <FaFilter />
                                    </Button>

                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            {/* Table of mouvements */}
            <Row>
                <Col>
                    <Card className="shadow-sm">
                        <CardBody>
                            {loading ? (
                                <div className="text-center my-4"><Spinner color="primary" /></div>
                            ) : error ? (
                                <div className="text-danger">Erreur lors du chargement des lignes.</div>
                            ) : (
                                <Table responsive bordered hover size="sm">
                                    <thead className="table-light">
                                        <tr>
                                            <th>N° BL</th>
                                            <th>Date Opération</th>
                                            <th>Article</th>
                                            <th>Quantité</th>
                                            <th>Unité</th>
                                            <th>Prix unitaire</th>
                                            <th>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lignes && lignes.length > 0 ? (
                                            lignes.map((m, idx) => (
                                                <tr key={idx} className={idx === 0 ? 'fw-bold' : ''}>
                                                    <td>{m.blNumero}</td>
                                                    <td>{m.blDateCreation}</td>
                                                    <td>{m.articleCode} / {m.articleCategorie} - {m.articleNom}</td>
                                                    <td>{m.quantiteLivree}</td>
                                                    <td>{unites.find(u => u.code === m.unite)?.libelle || m.unite}</td>
                                                    <td>{m.prixUnitaireTTC}</td>
                                                    <td>{m.montantTTC}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={7} className="text-center py-5">
                                                    <div className="d-flex flex-column align-items-center justify-content-center">
                                                        <span style={{ fontSize: '2rem', color: '#adb5bd' }}>
                                                            <FaTruck />
                                                        </span>
                                                        <div className="mt-2 text-muted">
                                                            {t('delivery.noData')}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan={3} className="text-end fw-bold">Total</td>
                                            <td className="fw-bold">
                                                {lignes && lignes.length > 0
                                                    ? lignes.reduce((sum, m) => sum + (Number(m.quantiteLivree) || 0), 0)
                                                    : 0}
                                            </td>
                                            <td></td>
                                            <td></td>
                                            <td className="fw-bold">
                                                {lignes && lignes.length > 0
                                                    ? formatPrice(lignes.reduce((sum, m) => sum + (Number(m.montantTTC) || 0), 0))
                                                    : 0}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </Table>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

export default BLDetails;